import { Col } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import dayjs from 'dayjs'
import Show from 'common/components/Show2/Show'
import { useTimezone } from 'common/hooks/useTimezone'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { PatientSelection } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientSelection/PatientSelection'
import { EmptyRoomsList } from 'features/Home/Book/Booking/Schedule/PatientSchedule/EmptyRoomsList/EmptyRoomsList'
import { ScheduleCalendarWeek } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleWeekCalendar/ScheduleWeekCalendar'
import { ScheduleDoctorRail } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/ScheduleDoctorRail'
import {
  ALL_STAFF_INSTANCE_ID,
  buildScheduleInstanceKey,
  filterSchedulesForStaffFocus,
  getScheduleStaffFocusCaption,
  getVisibleScheduleStaff,
  isNoConsultationStaffSelected,
  type ScheduleStaffFocusCaption,
  type ScheduleStaffRole,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import { useScheduleHeight } from 'features/Home/Book/Booking/Schedule/PatientSchedule/useScheduleHeight'
import {
  ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS,
  INFO_CONSTANTS,
} from 'features/Home/constants/infoConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { ManageAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageAppointment'
import { ManageEhrAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageEhrAppointment'
import { useScheduleWeekActions } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekActions'
import { useScheduleWeekData } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekData'
import { type EScheduleView } from 'features/Schedule/pages/Schedule'
import { useSchedulePusher } from 'features/Schedule/pusher/useSchedulePusher'

import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import pageStyles from '../../schedule.module.scss'

type Props = {
  defaultActiveDate: string | null
  selectedConsultationStaffIds: string[] | null
  showCanceled: boolean
  scheduleView: EScheduleView
  scheduleMonthData?: ScheduleMonth
  zoomInterval: number
  staffRoleUsers?: ScheduleStaffRole[]
  onStaffFocusCaptionChange?: (caption: ScheduleStaffFocusCaption | null) => void
}

export const ScheduleWeekView = ({
  defaultActiveDate,
  selectedConsultationStaffIds,
  showCanceled,
  scheduleView,
  scheduleMonthData,
  zoomInterval,
  staffRoleUsers = [],
  onStaffFocusCaptionChange,
}: Props) => {
  const { timeZone } = useTimezone()

  useSchedulePusher(defaultActiveDate)

  const [editedPatient, setEditedPatient] = useState<IPatientSchedule | null>(null)
  const [addPatient, setAddPatient] = useState<boolean>(false)
  const [customAppointmentData, setCustomAppointmentData] = useState<{
    appointment: Date[]
    room: string
    date?: string
    consultationStaffId?: number
  } | null>(null)

  const {
    isLoadingWeek,
    isFetchingWeek,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    weekDays,
    eventsToSendNotification,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    byPassTemperatureCheck,
    rooms,
    appointmentColor,
    punctuality,
    patientBufferTime,
  } = useScheduleWeekData(defaultActiveDate, selectedConsultationStaffIds, showCanceled)

  const { updateAppointment, notifyMultiplePatients, isNotifying, isNotifyingMultiple } =
    useScheduleWeekActions()

  useEffect(() => {
    if (!editedPatient?.id) return

    const latestPatient = schedules.find((schedule) => schedule.id === editedPatient.id)
    if (!latestPatient || latestPatient === editedPatient) return

    const hasEncounterChanged =
      latestPatient.emr_encounter_id !== editedPatient.emr_encounter_id ||
      latestPatient.emr_encounter_uuid !== editedPatient.emr_encounter_uuid ||
      latestPatient.emr_updated_at !== editedPatient.emr_updated_at

    if (hasEncounterChanged) {
      setEditedPatient(latestPatient)
    }
  }, [schedules, editedPatient])

  const requestedRangeStart = useMemo(() => {
    if (!defaultActiveDate) return null
    return dayjs(defaultActiveDate).startOf('week').format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [defaultActiveDate])

  const isWeekDataSynced = useMemo(() => {
    if (!requestedRangeStart) return false
    const weekDayKeys = Object.keys(weekDays)
    if (weekDayKeys.length === 0) return false
    return weekDayKeys.some(
      (dayKey) =>
        dayjs(dayKey).startOf('week').format(DATE_FORMAT.PATIENT_TRACKER_DATE) ===
        requestedRangeStart,
    )
  }, [weekDays, requestedRangeStart])

  const isLoading = !defaultActiveDate || isLoadingWeek || (isFetchingWeek && !isWeekDataSynced)

  const visibleStaff = useMemo(
    () =>
      getVisibleScheduleStaff(
        consultationStaffs,
        schedules,
        selectedConsultationStaffIds,
        staffRoleUsers,
      ),
    [consultationStaffs, schedules, selectedConsultationStaffIds, staffRoleUsers],
  )

  const [focusedStaffIds, setFocusedStaffIds] = useState<number[]>([])

  useEffect(() => {
    setFocusedStaffIds([])
  }, [requestedRangeStart])

  useEffect(() => {
    const visibleIds = new Set(visibleStaff.map((staff) => staff.id))
    setFocusedStaffIds((current) => {
      const next = current.filter((staffId) => visibleIds.has(staffId))
      return next.length === current.length ? current : next
    })
  }, [visibleStaff])

  const focusedStaff = useMemo(
    () => visibleStaff.filter((staff) => focusedStaffIds.includes(staff.id)),
    [focusedStaffIds, visibleStaff],
  )

  const displayedSchedules = useMemo(
    () => filterSchedulesForStaffFocus(schedules, focusedStaffIds),
    [focusedStaffIds, schedules],
  )

  const calendarHeight = useScheduleHeight()

  useEffect(() => {
    if (!onStaffFocusCaptionChange) return
    onStaffFocusCaptionChange(getScheduleStaffFocusCaption(focusedStaff))
  }, [focusedStaff, onStaffFocusCaptionChange])

  useEffect(() => {
    return () => onStaffFocusCaptionChange?.(null)
  }, [onStaffFocusCaptionChange])

  const handleCloseModal = () => {
    setAddPatient(false)
    setEditedPatient(null)
    setCustomAppointmentData(null)
  }

  const createScheduleWithCustomAppointment = (
    start: Date,
    end: Date,
    date: string,
    consultationStaffId?: number,
  ): void => {
    const defaultRoom = rooms[0]

    const appointmentData = {
      appointment: [start, end],
      room: `${defaultRoom.label}/${defaultRoom.value}`,
      date,
      consultationStaffId,
    }
    setCustomAppointmentData(appointmentData)
    setAddPatient(true)
  }

  const handleToggleFocusedStaff = useCallback((staffId: number) => {
    setFocusedStaffIds((current) =>
      current.includes(staffId) ? current.filter((id) => id !== staffId) : [...current, staffId],
    )
  }, [])

  const handleClearFocusedStaff = useCallback(() => {
    setFocusedStaffIds([])
  }, [])

  const prefillConsultationStaffId =
    focusedStaffIds.length === 1
      ? focusedStaffIds[0]
      : visibleStaff.length === 1
        ? visibleStaff[0].id
        : undefined

  const sharedInstanceKey = buildScheduleInstanceKey({
    view: 'week',
    date: requestedRangeStart ?? '',
    staffId: ALL_STAFF_INSTANCE_ID,
  })

  const disabledInputsManageAppointment = useMemo(() => {
    if (!editedPatient) return false
    return !ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS.includes(editedPatient?.status.code)
  }, [editedPatient])

  const handleUpdateEvents = useCallback(
    async (adjustedData: any, event: any, newDate?: string) => {
      return await updateAppointment(adjustedData, newDate || defaultActiveDate, event.id)
    },
    [defaultActiveDate],
  )

  const manageAppointmentTitle = useMemo(() => {
    if (editedPatient) {
      return INFO_CONSTANTS.EDIT_APPOINTMENT
    }
    return INFO_CONSTANTS.ADD_APPOINTMENT
  }, [editedPatient, defaultActiveDate])

  return (
    <Col xs={24} className={pageStyles.scheduleBody}>
      <Show>
        <Show.When isVisible={isLoading}>
          <PatientScheduleSkeleton />
        </Show.When>
        <Show.When isVisible={isNoConsultationStaffSelected(selectedConsultationStaffIds)}>
          <EmptyRoomsList title={INFO_CONSTANTS.NO_CONSULTATION_STAFF_SELECTED} />
        </Show.When>
        <Show.Else>
          {visibleStaff.length > 0 ? (
            <div className={pageStyles.doctorDayLayout} style={{ height: calendarHeight }}>
              <ScheduleDoctorRail
                staff={visibleStaff}
                focusedStaffIds={focusedStaffIds}
                onToggleStaff={handleToggleFocusedStaff}
                onClear={handleClearFocusedStaff}
              />
              <div className={pageStyles.doctorDayCalendar}>
                <ScheduleCalendarWeek
                  key={sharedInstanceKey}
                  sendInfoPatientsNotification={notifyMultiplePatients}
                  setEditedPatient={setEditedPatient}
                  createCustomAppointment={(start, end, date) =>
                    createScheduleWithCustomAppointment(
                      start,
                      end,
                      date,
                      prefillConsultationStaffId,
                    )
                  }
                  handleUpdateEvents={handleUpdateEvents}
                  showConsultationStaffInitials
                  prepopulateTimeOnClick={defaultAppointmentTime}
                  dayInterval={timeInterval}
                  interval={zoomInterval}
                  weekDays={weekDays}
                  eventsToSendNotification={eventsToSendNotification}
                  isSendingNotifications={isNotifying || isNotifyingMultiple}
                  timeZone={timeZone}
                  schedules={displayedSchedules}
                  patientStatuses={patientStatuses}
                  appointmentCategories={appointmentCategories}
                  appointmentColor={appointmentColor}
                  hasEhrBooking={hasEhrBooking}
                  instanceKey={sharedInstanceKey}
                  focusedStaffIds={focusedStaffIds}
                  scaleColumnsByOverlap={false}
                />
              </div>
            </div>
          ) : null}
        </Show.Else>
      </Show>

      <Show>
        <Show.When isVisible={hasEhrBooking}>
          <ManageEhrAppointment
            patientBufferTime={patientBufferTime}
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={customAppointmentData?.date || defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
            weekDays={weekDays}
            punctuality={punctuality}
            scheduleMonthData={scheduleMonthData}
          />
        </Show.When>
        <Show.Else>
          <ManageAppointment
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={customAppointmentData?.date || defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
            weekDays={weekDays}
          />
        </Show.Else>
      </Show>

      <PatientSelection />
    </Col>
  )
}
